import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";
const toBool = (v) => v === 1 || v === "1" || v === true;

const TablaProgreso = ({ onClose, niveles = [], usuario }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Filas normalizadas: [{ nivelId, nivelLabel, tema, completado, vacio? }]
  const [filas, setFilas] = useState([]);
  const [nivelesAbiertos, setNivelesAbiertos] = useState({});

  const usuarioId = usuario?.id || null;

  // ¿Es docente?
  const esDocente = useMemo(() => {
    const raw =
      (usuario?.rol ||
        usuario?.role ||
        usuario?.tipo ||
        usuario?.perfil ||
        "") + "";
    const v = raw.toLowerCase();
    return (
      v.includes("docente") ||
      v.includes("teacher") ||
      v.includes("profesor") ||
      v.includes("profesora")
    );
  }, [usuario]);

  // Niveles a mostrar:
  // - Docente: quitar diagnóstico (orden = 1), ordenar por 'orden' asc y tomar 5 (orden 2..6).
  // - Otros roles: tal cual llegan.
  const nivelesParaMostrar = useMemo(() => {
    if (!Array.isArray(niveles)) return [];

    if (!esDocente) return niveles;

    // Usa 'orden' de la BD para excluir solo el diagnóstico y tomar exactamente 5
    const conOrden = niveles
      .map((n) => ({ ...n, _orden: Number(n?.orden) }))
      .filter((n) => Number.isFinite(n._orden));

    const soloDocente = conOrden
      .filter((n) => n._orden >= 2) // excluye orden=1 (diagnóstico/Nivel 0)
      .sort((a, b) => a._orden - b._orden) // respeta orden 2..6
      .slice(0, 5); // exactamente 5 niveles

    return soloDocente;
  }, [niveles, esDocente]);

  // Dependencia ESTABLE: sólo ids de los niveles que vamos a mostrar
  const nivelesKey = useMemo(
    () =>
      Array.isArray(nivelesParaMostrar)
        ? nivelesParaMostrar.map((n) => n.id).join(",")
        : "",
    [nivelesParaMostrar]
  );

  // Etiqueta visual SIEMPRE por posición dentro de 'nivelesParaMostrar'
  const labelDeNivel = (_n, idx) => `Nivel ${idx + 1}`;

  useEffect(() => {
    let cancelled = false;

    const cargarProgreso = async () => {
      if (
        !usuarioId ||
        !Array.isArray(nivelesParaMostrar) ||
        nivelesParaMostrar.length === 0
      )
        return;

      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem("token");
        if (!token) throw new Error("Token no encontrado");

        // Pedimos temas siguiendo EXACTAMENTE el orden de 'nivelesParaMostrar'
        const peticiones = nivelesParaMostrar.map((n, idx) =>
          axios
            .get(`${API_BASE_URL}/api/temas/info/${n.id}/${usuarioId}`, {
              headers: { Authorization: `Bearer ${token}` },
              params: { t: Date.now() }, // evita cache
            })
            .then((res) => ({
              nivelId: n.id,
              nivelLabel: labelDeNivel(n, idx),
              desafioAprobado: toBool(n?.completado), // 5/5 desafío final
              temas: Array.isArray(res.data) ? res.data : [],
            }))
        );

        const respuestas = await Promise.all(peticiones);
        if (cancelled) return;

        const normalizado = [];
        respuestas.forEach(
          ({ nivelId, nivelLabel, temas, desafioAprobado }) => {
            // Si no hay temas, lo mostramos igual (para que exista Nivel 5 aunque esté vacío)
            if (!temas.length) {
              normalizado.push({
                nivelId,
                nivelLabel,
                tema: "— Sin temas registrados —",
                completado: false,
                vacio: true,
              });
            } else {
              // Temas del nivel
              temas.forEach((d, i) => {
                const completado =
                  d?.completado === 1 ||
                  d?.completado === "1" ||
                  d?.completado === true;
                const temaNombre =
                  d?.tema || d?.nombre || d?.titulo || `Tema ${i + 1}`;
                normalizado.push({
                  nivelId,
                  nivelLabel,
                  tema: temaNombre,
                  completado,
                });
              });
            }

            // ➕ Fila fija del Desafío final (SIEMPRE cuenta)
            normalizado.push({
              nivelId,
              nivelLabel,
              tema: "Desafío final",
              completado: !!desafioAprobado, // true sólo si 5/5
              isDesafio: true,
            });
          }
        );

        setFilas(normalizado);
      } catch (e) {
        console.error("Error al cargar progreso:", e);
        if (!cancelled)
          setError("Error al cargar el progreso. Intenta de nuevo.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    cargarProgreso();
    return () => {
      cancelled = true;
    };
  }, [nivelesKey, usuarioId, nivelesParaMostrar]);

  // Agrupación por ID de nivel (clave estable)
  const gruposPorId = useMemo(() => {
    const g = {};
    filas.forEach((item) => {
      if (!g[item.nivelId])
        g[item.nivelId] = { label: item.nivelLabel, items: [] };
      g[item.nivelId].items.push(item);
    });
    return g;
  }, [filas]);

  const toggleNivel = (nivelId) => {
    setNivelesAbiertos((prev) => ({ ...prev, [nivelId]: !prev[nivelId] }));
  };

  const sinDatos = !loading && !error && nivelesParaMostrar.length === 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-auto p-4">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-3xl relative">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-red-600 font-bold text-xl"
        >
          &times;
        </button>

        <h2 className="text-xl font-bold mb-4 text-green-700">
          Progreso por nivel y tema
        </h2>

        {loading && <p className="text-sm text-gray-600">Cargando…</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}

        {!loading && !error && (
          <div className="border rounded-md overflow-hidden">
            {sinDatos ? (
              <div className="p-4 text-sm text-gray-600">
                {!usuarioId || nivelesParaMostrar.length === 0
                  ? "Preparando datos…"
                  : "No hay datos para mostrar."}
              </div>
            ) : (
              // Render en el MISMO orden de 'nivelesParaMostrar'
              nivelesParaMostrar.map((n, idx) => {
                const grp = gruposPorId[n.id] || {
                  label: labelDeNivel(n, idx),
                  items: [],
                };
                const items = grp.items;
                const total = items.filter((t) => !t.vacio).length; // excluye "Sin temas..."
                const hechos = items.filter((t) => t.completado).length;

                return (
                  <div key={n.id} className="border-b">
                    <button
                      onClick={() => toggleNivel(n.id)}
                      className="w-full text-left px-4 py-3 bg-green-900 hover:bg-green-200 font-semibold flex justify-between items-center"
                    >
                      <span>{grp.label}</span>
                      <span className="text-sm text-green-300">
                        {total > 0 ? `${hechos}/${total} completados` : "0/0"}
                      </span>
                    </button>

                    {nivelesAbiertos[n.id] && (
                      <table className="w-full text-sm text-left">
                        <thead className="bg-green-700 text-white">
                          <tr>
                            <th className="px-4 py-2 border">Tema</th>
                            <th className="px-4 py-2 border">Estado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((fila, index) => (
                            <tr key={`${n.id}-${index}`} className="text-black">
                              <td className="px-4 py-2 border">{fila.tema}</td>
                              <td className="px-4 py-2 border">
                                {fila.vacio ? (
                                  <span className="text-gray-500">
                                    Sin datos
                                  </span>
                                ) : fila.completado ? (
                                  <span className="text-green-700 font-semibold">
                                    Aprobado
                                  </span>
                                ) : (
                                  <span className="text-gray-700">
                                    No completado
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TablaProgreso;
